/**
 * Headless dagre layered layout for the schema graph.
 *
 * Produces a clean left-to-right (or top-to-bottom) layered arrangement that uses
 * the full canvas width and spaces nodes by their real card size — replacing the
 * old hand-rolled 4-column grid that cramped everything into a central strip.
 *
 * Returns a plain `{ id → {x, y} }` map (top-left coordinates for React Flow);
 * dagre computes node CENTERS, so we shift by half the node size.
 */

import dagre from 'dagre';

export interface DagreNodeInput {
  id: string;
  /** React Flow node type ('schemaNode' = big card, else circular KG node). */
  type?: string;
  width?: number;
  height?: number;
}

export interface DagreEdgeInput {
  source: string;
  target: string;
}

export interface DagreOptions {
  rankdir?: 'LR' | 'TB' | 'BT' | 'RL';
  /** Separation between nodes in the same rank. */
  nodesep?: number;
  /** Separation between ranks (layers). */
  ranksep?: number;
  marginx?: number;
  marginy?: number;
  cardSize?: { width: number; height: number };
  nodeSize?: { width: number; height: number };
}

/**
 * Compute a stable dagre layered layout. Self-loops and edges to unknown nodes
 * are ignored. Deterministic run-to-run.
 */
export function computeDagreLayout(
  nodes: DagreNodeInput[],
  edges: DagreEdgeInput[],
  options: DagreOptions = {}
): Record<string, { x: number; y: number }> {
  const {
    // Top-to-bottom so each rank is a horizontal ROW — same-rank nodes spread
    // across the full width (the schema graph is shallow/wide, so LR would stack
    // them into a tall narrow strip). ranksep is the vertical gap between rows;
    // nodesep is the horizontal gap between siblings in a row.
    rankdir = 'TB',
    nodesep = 70,
    ranksep = 140,
    marginx = 40,
    marginy = 40,
    cardSize = { width: 300, height: 240 },
    nodeSize = { width: 120, height: 120 },
  } = options;

  const positions: Record<string, { x: number; y: number }> = {};
  if (nodes.length === 0) return positions;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir, nodesep, ranksep, marginx, marginy });
  g.setDefaultEdgeLabel(() => ({}));

  const ids = new Set(nodes.map((n) => n.id));
  for (const n of nodes) {
    const isCard = n.type === 'schemaNode';
    const width = n.width ?? (isCard ? cardSize.width : nodeSize.width);
    const height = n.height ?? (isCard ? cardSize.height : nodeSize.height);
    g.setNode(n.id, { width, height });
  }
  for (const e of edges) {
    if (e.source !== e.target && ids.has(e.source) && ids.has(e.target)) {
      g.setEdge(e.source, e.target);
    }
  }

  dagre.layout(g);

  for (const id of ids) {
    const node = g.node(id);
    if (node) {
      // dagre gives node centers; React Flow positions are top-left.
      positions[id] = { x: node.x - node.width / 2, y: node.y - node.height / 2 };
    }
  }
  return positions;
}

/**
 * Apply {@link computeDagreLayout} directly to an array of React Flow nodes
 * (objects carrying `id`, `type`, `position`), returning new nodes with updated
 * `position`. Convenience wrapper for the graph component's layout call sites.
 */
export function layoutWithDagre<
  T extends { id: string; type?: string; position: { x: number; y: number } }
>(nodes: T[], edges: DagreEdgeInput[], options: DagreOptions = {}): T[] {
  const positions = computeDagreLayout(
    nodes.map((n) => ({ id: n.id, type: n.type })),
    edges,
    options
  );
  return nodes.map((n) => ({ ...n, position: positions[n.id] ?? n.position }));
}
